/* BUSINESS PERFORMANCE — the Inventory and Marketing pages (2026-10-24; Max: "thêm trang report về inventory và marketing
   vào đây nhé => kiểm bảng phân tích các chỉ số á", and "Business performance sẽ trình bày data dưới dạng bảng nhiều hơn
   còn data explore là dưới dạng chart"). Picked from the page's own Report box; each page has its own two or three
   controls (the sales filters do not apply to stock or ads) and draws its figures as the sales sections are drawn —
   the same section frame, table and ▲/▼ growth cells — from /api/bp/inventory and /api/bp/marketing (bizTables.js),
   which read the same sources as the Data Explorer's inventory and marketing datasets. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './BizTables.css';

export const BP_TABLES_ON = process.env.REACT_APP_BP_TABLES === '1';
export const BP_TABLES_OFF_MARK = BP_TABLES_ON ? '' : '__bptables_off';
export const BIZ_PAGES = ['inventory', 'marketing', 'pole'];
const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/bp`;
const CURRENCIES = ['USD', 'KRW', 'SGD', 'EUR', 'JPY'];
const SYMBOL = { USD: '$', KRW: '₩', SGD: 'S$', EUR: '€', JPY: '¥' };
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const fmtDay = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${MON[Number(m) - 1]}/${y}`;
};
export const fmtYm = (ym) => {
  if (!ym) return '—';
  const [y, m] = ym.split('-');
  return `${MON[Number(m) - 1]} ${y}`;
};
export const int = (v) => (v == null || !Number.isFinite(Number(v)) ? '-' : Math.round(Number(v)).toLocaleString('en-US'));
const money = (v, sym = '$', dec = 0) => {
  if (v == null || !Number.isFinite(Number(v))) return '-';
  const x = Number(v);
  if (!x) return '-';
  const s = Math.abs(x).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
  return `${x < 0 ? '-' : ''}${sym}${s}`;
};
const pct = (v, dec = 1) => (v == null || !Number.isFinite(v) ? '-' : `${(v * 100).toFixed(dec)}%`);
const ratio = (a, b) => (b ? a / b : null);
const growth = (cur, prev) => (prev ? (cur - prev) / Math.abs(prev) : null);

/* ▲ / ▼ in the colour the sales sections use; `lowerIsBetter` turns it round (stock over a year going down is good) */
function Growth({ v, lowerIsBetter = false }) {
  if (v == null || !Number.isFinite(v)) return <td className="bz-num">-</td>;
  const good = lowerIsBetter ? v < 0 : v > 0;
  const cls = v === 0 ? '' : good ? 'good' : 'negative';
  return <td className={`bz-num ${cls}`}>{v > 0 ? '▲ ' : v < 0 ? '▼ ' : ''}{(v * 100).toFixed(1)}%</td>;
}

export function Section({ title, right, note, children }) {
  return (
    <section className="srd-section bz-section">
      <div className="srd-section-header">
        <h3>{title}</h3>
        {right ? <strong>{right}</strong> : null}
      </div>
      {note ? <p className="bz-note">{note}</p> : null}
      <div className="bz-wrap">{children}</div>
    </section>
  );
}

export const useFetch = (url) => {
  const [state, setState] = useState({ data: null, error: '', loading: true });
  const seq = useRef(0);
  const run = useCallback(() => {
    const id = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: '' }));
    fetch(url, { credentials: 'include' })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.error || `HTTP ${r.status}`);
        return body;
      })
      .then((data) => { if (id === seq.current) setState({ data, error: '', loading: false }); })
      .catch((e) => { if (id === seq.current) setState((s) => ({ ...s, error: e.message, loading: false })); });
  }, [url]);
  useEffect(() => { run(); }, [run]);
  return { ...state, reload: run };
};

/* A long table scrolls in its own box with its header frozen (see BizTables.css); the box is as tall as the screen below
   the page's sticky Report panel, whose height changes when its controls wrap — so it is measured, into --bz-top. */
export const useBzTop = () => {
  useEffect(() => {
    const panel = document.querySelector('.srd-controls');
    if (!panel) return undefined;
    const set = () => {
      const top = parseFloat(getComputedStyle(panel).top) || 0;
      document.documentElement.style.setProperty('--bz-top', `${Math.round(top + panel.getBoundingClientRect().height)}px`);
    };
    set();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(set) : null;
    if (ro) ro.observe(panel);
    window.addEventListener('resize', set);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', set); };
  }, []);
};

/* ================================================================ Inventory */
export function BizInventory() {
  const { t } = useTranslation();
  useBzTop();
  const [day, setDay] = useState('');
  const { data, error, loading, reload } = useFetch(`${API}/inventory${day ? `?day=${day}` : ''}`);
  const d = data || {};
  const rows = d.byEntity || [];
  const tot = rows.reduce((a, x) => {
    for (const k of ['qty', 'usd', 'transitQty', 'transitUsd', 'b90', 'b180', 'b365', 'b1y', 'skus']) a[k] = (a[k] || 0) + (x[k] || 0);
    a.prevUsd = (a.prevUsd || 0) + (x.prevUsd || 0);
    a.ageW = (a.ageW || 0) + (x.avgAge == null ? 0 : x.avgAge * x.qty);
    a.ageQ = (a.ageQ || 0) + (x.avgAge == null ? 0 : x.qty);
    return a;
  }, {});
  const totUsd = tot.usd || 0;
  const cats = d.byCategory || [];
  const catTot = cats.reduce((a, x) => a + x.usd, 0);

  return (
    <div className="bz-page" data-bz="inventory">
      <div className="bz-controls">
        <label className="srd-field">
          <span>{t('xBiz.day')}</span>
          <select value={day || d.day || ''} onChange={(e) => setDay(e.target.value)} disabled={!d.days?.length}>
            {(d.days || []).map((x) => (
              <option key={x.day} value={x.day}>{fmtDay(x.day)}{x.friday ? ` · ${t('xBiz.friday')}` : ''}</option>
            ))}
          </select>
        </label>
        <button type="button" className="srd-primary bz-reload" onClick={reload} disabled={loading}>{loading ? t('xBiz.loading') : t('xBiz.reload')}</button>
      </div>
      <section className="srd-summary">
        <h2>{t('xBiz.invTitle')}</h2>
        <p>{d.day ? t('xBiz.invSub', { d: fmtDay(d.day), p: d.prev ? fmtDay(d.prev) : '—' }) : ' '}</p>
      </section>
      {error && <div className="srd-message error">{error}</div>}
      {loading && !data && <div className="srd-message">{t('xBiz.loading')}</div>}
      {data && !rows.length && <div className="srd-empty">{t('xBiz.noData')}</div>}

      {rows.length > 0 && (
        <Section title={t('xBiz.secEntity')} right={d.prev ? t('xBiz.vsDay', { d: fmtDay(d.prev) }) : null}>
          <table className="bz-table" data-bz-table="entity">
            <thead>
              <tr>
                <th className="bz-first">{t('xBiz.entity')}</th>
                <th>{t('xBiz.qty')}</th><th>{t('xBiz.usd')}</th><th>{t('xBiz.share')}</th>
                <th>{t('xBiz.chgUsd')}</th><th>{t('xBiz.chgPct')}</th>
                <th className="bz-sep">{t('xBiz.transitQty')}</th><th>{t('xBiz.transitUsd')}</th>
                <th className="bz-sep">{t('xBiz.b90')}</th><th>{t('xBiz.b180')}</th><th>{t('xBiz.b365')}</th><th>{t('xBiz.b1y')}</th>
                <th>{t('xBiz.over1yPct')}</th>
                <th className="bz-sep">{t('xBiz.skus')}</th><th>{t('xBiz.avgAge')}</th>
              </tr>
            </thead>
            <tbody>
              {[...rows, { entity: t('xBiz.total'), total: true, ...tot, avgAge: tot.ageQ ? tot.ageW / tot.ageQ : null }].map((x) => (
                <tr key={x.entity} className={x.total ? 'total' : ''}>
                  <td className="bz-first">{x.entity}</td>
                  <td className="bz-num">{int(x.qty)}</td>
                  <td className="bz-num">{money(x.usd)}</td>
                  <td className="bz-num">{pct(ratio(x.usd, totUsd))}</td>
                  <td className={`bz-num ${x.prevUsd != null && x.usd - x.prevUsd < 0 ? 'negative' : ''}`}>{x.prevUsd == null ? '-' : money(x.usd - x.prevUsd)}</td>
                  <Growth v={x.prevUsd == null ? null : growth(x.usd, x.prevUsd)} />
                  <td className="bz-num bz-sep">{int(x.transitQty)}</td>
                  <td className="bz-num">{money(x.transitUsd)}</td>
                  <td className="bz-num bz-sep">{money(x.b90)}</td>
                  <td className="bz-num">{money(x.b180)}</td>
                  <td className="bz-num">{money(x.b365)}</td>
                  <td className="bz-num">{money(x.b1y)}</td>
                  <td className={`bz-num ${ratio(x.b1y, x.usd) > 0.3 ? 'bz-warn' : ''}`}>{pct(ratio(x.b1y, x.usd))}</td>
                  <td className="bz-num bz-sep">{int(x.skus)}</td>
                  <td className="bz-num">{int(x.avgAge)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {cats.length > 0 && (
        <Section title={t('xBiz.secCategory')}>
          <table className="bz-table" data-bz-table="category">
            <thead>
              <tr>
                <th className="bz-first">{t('xBiz.category')}</th>
                <th>{t('xBiz.qty')}</th><th>{t('xBiz.usd')}</th><th>{t('xBiz.share')}</th>
                <th>{t('xBiz.chgUsd')}</th><th>{t('xBiz.chgPct')}</th>
                <th>{t('xBiz.b1y')}</th><th>{t('xBiz.over1yPct')}</th><th>{t('xBiz.skus')}</th>
              </tr>
            </thead>
            <tbody>
              {cats.map((x) => (
                <tr key={x.category || '_'}>
                  <td className="bz-first">{x.category || <i className="bz-muted">{t('xBiz.notClassified')}</i>}</td>
                  <td className="bz-num">{int(x.qty)}</td>
                  <td className="bz-num">{money(x.usd)}</td>
                  <td className="bz-num">{pct(ratio(x.usd, catTot))}</td>
                  <td className={`bz-num ${x.prevUsd != null && x.usd - x.prevUsd < 0 ? 'negative' : ''}`}>{x.prevUsd == null ? '-' : money(x.usd - x.prevUsd)}</td>
                  <Growth v={x.prevUsd == null ? null : growth(x.usd, x.prevUsd)} />
                  <td className="bz-num">{money(x.b1y)}</td>
                  <td className={`bz-num ${ratio(x.b1y, x.usd) > 0.3 ? 'bz-warn' : ''}`}>{pct(ratio(x.b1y, x.usd))}</td>
                  <td className="bz-num">{int(x.skus)}</td>
                </tr>
              ))}
              <tr className="total">
                <td className="bz-first">{t('xBiz.total')}</td>
                <td className="bz-num">{int(cats.reduce((a, x) => a + x.qty, 0))}</td>
                <td className="bz-num">{money(catTot)}</td>
                <td className="bz-num">100.0%</td>
                <td className="bz-num">{money(catTot - cats.reduce((a, x) => a + (x.prevUsd || 0), 0))}</td>
                <Growth v={growth(catTot, cats.reduce((a, x) => a + (x.prevUsd || 0), 0))} />
                <td className="bz-num">{money(cats.reduce((a, x) => a + x.b1y, 0))}</td>
                <td className="bz-num">{pct(ratio(cats.reduce((a, x) => a + x.b1y, 0), catTot))}</td>
                <td className="bz-num" />
              </tr>
            </tbody>
          </table>
        </Section>
      )}

      {(d.oldest || []).length > 0 && (
        <Section title={t('xBiz.secOldest')}>
          <table className="bz-table" data-bz-table="oldest">
            <thead>
              <tr>
                <th className="bz-first">{t('xBiz.entity')}</th><th className="bz-text">{t('xBiz.sku')}</th>
                <th className="bz-text">{t('xBiz.product')}</th><th className="bz-text">{t('xBiz.color')}</th>
                <th className="bz-text">{t('xBiz.category')}</th>
                <th>{t('xBiz.qty')}</th><th>{t('xBiz.usd')}</th><th>{t('xBiz.b1y')}</th><th>{t('xBiz.avgAge')}</th>
              </tr>
            </thead>
            <tbody>
              {d.oldest.map((x) => (
                <tr key={`${x.entity}|${x.sku}`}>
                  <td className="bz-first">{x.entity}</td>
                  <td className="bz-text bz-code">{x.sku}</td>
                  <td className="bz-text">{x.product}</td>
                  <td className="bz-text">{x.color}</td>
                  <td className="bz-text">{x.category || <i className="bz-muted">{t('xBiz.notClassified')}</i>}</td>
                  <td className="bz-num">{int(x.qty)}</td>
                  <td className="bz-num">{money(x.usd)}</td>
                  <td className="bz-num">{money(x.b1y)}</td>
                  <td className="bz-num">{int(x.avgAge)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {d.pole?.rows?.length > 0 && (() => {
        const pr = d.pole.rows;
        const pt = pr.reduce((a, x) => { for (const k of ['endQty', 'nowQty', 'sold', 'avgMonth', 'poTotal']) a[k] = (a[k] || 0) + x[k]; return a; }, {});
        const m = fmtYm(d.pole.baseMonth);
        return (
          <Section title={t('xBiz.secPole')} note={t('xBiz.poleSub', { d: fmtDay(d.pole.day), m })}>
            <table className="bz-table" data-bz-table="pole">
              <thead>
                <tr>
                  <th className="bz-first">{t('xBiz.entity')}</th>
                  <th>{t('xBiz.endQty', { m })}</th><th>{t('xBiz.sold')}</th><th>{t('xBiz.nowQty')}</th>
                  <th>{t('xBiz.avgMonth')}</th><th>{t('xBiz.mos')}</th><th>{t('xBiz.openPo')}</th><th>{t('xBiz.mosPo')}</th>
                </tr>
              </thead>
              <tbody>
                {[...pr, { entity: t('xBiz.total'), total: true, ...pt }].map((x) => {
                  const mos = ratio(x.nowQty, x.avgMonth);
                  return (
                    <tr key={x.entity} className={x.total ? 'total' : ''}>
                      <td className="bz-first">{x.entity}</td>
                      <td className="bz-num">{int(x.endQty)}</td>
                      <td className="bz-num">{int(x.sold)}</td>
                      <td className="bz-num">{int(x.nowQty)}</td>
                      <td className="bz-num">{int(x.avgMonth)}</td>
                      <td className={`bz-num ${mos != null && mos < 2 ? 'bz-warn' : ''}`}>{mos == null ? '-' : mos.toFixed(1)}</td>
                      <td className="bz-num">{int(x.poTotal)}</td>
                      <td className="bz-num">{x.avgMonth ? ((x.nowQty + x.poTotal) / x.avgMonth).toFixed(1) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Section>
        );
      })()}
    </div>
  );
}

/* ================================================================ Marketing */
const mktCells = (x, sym) => ({
  ctr: ratio(x.clicks, x.impressions),
  cpc: ratio(x.spend, x.clicks),
  cpm: x.impressions ? (x.spend * 1000) / x.impressions : null,
  roas: ratio(x.attrRev, x.claimSpend),
  mer: x.storeRev ? ratio(x.storeRev, x.spend) : null,
  conv: ratio(x.webOrders, x.sessions),
  sym,
});

export function BizMarketing() {
  const { t } = useTranslation();
  useBzTop();
  const [month, setMonth] = useState('');
  const [currency, setCurrency] = useState('USD');
  const { data, error, loading, reload } = useFetch(`${API}/marketing?currency=${currency}${month ? `&month=${month}` : ''}`);
  const d = data || {};
  const sym = SYMBOL[d.currency || currency] || '';
  const dec = 0;
  /* a won or a yen has no cents: unit costs keep two decimals only in the other currencies */
  const dec2 = ['KRW', 'JPY'].includes(d.currency || currency) ? 0 : 2;
  const m = d.byMarket || [];
  const sum = (rows, part) => rows.reduce((a, r) => {
    const x = part ? r[part] : r;
    if (!x) return a;
    for (const k of Object.keys(x)) if (typeof x[k] === 'number') a[k] = (a[k] || 0) + x[k];
    return a;
  }, {});
  const tCur = sum(m, 'cur'); const tPrev = sum(m, 'prev'); const tLy = sum(m, 'ly');
  const hasPrev = m.some((x) => x.prev); const hasLy = m.some((x) => x.ly);

  const marketRow = (key, label, cur, prev, ly, total) => {
    const c = mktCells(cur, sym);
    return (
      <tr key={key} className={total ? 'total' : ''}>
        <td className="bz-first">{label}</td>
        <td className="bz-num">{money(cur.spend, sym, dec)}</td>
        <Growth v={prev ? growth(cur.spend, prev.spend) : null} />
        <td className="bz-num">{int(cur.impressions)}</td>
        <td className="bz-num">{int(cur.clicks)}</td>
        <td className="bz-num">{pct(c.ctr, 2)}</td>
        <td className="bz-num">{money(c.cpc, sym, dec2)}</td>
        <td className="bz-num bz-sep">{money(cur.attrRev, sym, dec)}</td>
        <td className="bz-num">{c.roas == null ? '-' : c.roas.toFixed(2)}</td>
        <td className="bz-num bz-sep">{money(cur.emailRev, sym, dec)}</td>
        <td className="bz-num">{int(cur.sessions)}</td>
        <td className="bz-num">{pct(c.conv, 2)}</td>
        <td className="bz-num bz-sep">{money(cur.storeRev, sym, dec)}</td>
        <Growth v={prev ? growth(cur.storeRev, prev.storeRev) : null} />
        <Growth v={ly ? growth(cur.storeRev, ly.storeRev) : null} />
        <td className="bz-num">{c.mer == null ? '-' : c.mer.toFixed(2)}</td>
      </tr>
    );
  };

  return (
    <div className="bz-page" data-bz="marketing">
      <div className="bz-controls">
        <label className="srd-field">
          <span>{t('xBiz.month')}</span>
          <select value={month || d.month || ''} onChange={(e) => setMonth(e.target.value)} disabled={!d.months?.length}>
            {(d.months || []).map((x) => <option key={x} value={x}>{fmtYm(x)}</option>)}
          </select>
        </label>
        <label className="srd-field">
          <span>{t('xBiz.currency')}</span>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <button type="button" className="srd-primary bz-reload" onClick={reload} disabled={loading}>{loading ? t('xBiz.loading') : t('xBiz.reload')}</button>
      </div>
      <section className="srd-summary">
        <h2>{t('xBiz.mktTitle')}</h2>
        <p>{d.month ? t('xBiz.mktSub', {
          m: fmtYm(d.month), c: d.currency, through: d.through ? t('xBiz.through', { d: fmtDay(d.through) }) : '',
          p: fmtYm(d.ranges?.prev?.label), ly: fmtYm(d.ranges?.ly?.label),
        }) : ' '}</p>
      </section>
      {error && <div className="srd-message error">{error}</div>}
      {loading && !data && <div className="srd-message">{t('xBiz.loading')}</div>}

      {m.length > 0 && (
        <Section title={t('xBiz.secMarket')} note={t('xBiz.noteMkt')}>
          <table className="bz-table" data-bz-table="market">
            <thead>
              <tr>
                <th className="bz-first">{t('xBiz.market')}</th>
                <th>{t('xBiz.spend')}</th><th>{t('xBiz.spendMoM')}</th><th>{t('xBiz.impressions')}</th><th>{t('xBiz.clicks')}</th>
                <th>{t('xBiz.ctr')}</th><th>{t('xBiz.cpc')}</th>
                <th className="bz-sep">{t('xBiz.attrRev')}</th><th title={t('xBiz.roasHint')}>{t('xBiz.roas')} ⓘ</th>
                <th className="bz-sep">{t('xBiz.emailRev')}</th><th>{t('xBiz.sessions')}</th><th>{t('xBiz.webConv')}</th>
                <th className="bz-sep">{t('xBiz.storeRev')}</th><th>{t('xBiz.storeMoM')}</th><th>{t('xBiz.storeLy')}</th>
                <th title={t('xBiz.merHint')}>{t('xBiz.mer')} ⓘ</th>
              </tr>
            </thead>
            <tbody>
              {m.map((x) => marketRow(x.market, x.market, x.cur, x.prev, x.ly))}
              {marketRow('_total', t('xBiz.total'), tCur, hasPrev ? tPrev : null, hasLy ? tLy : null, true)}
            </tbody>
          </table>
        </Section>
      )}

      {(d.byPlatform || []).length > 0 && (
        <Section title={t('xBiz.secPlatform')}>
          <table className="bz-table" data-bz-table="platform">
            <thead>
              <tr>
                <th className="bz-first">{t('xBiz.platform')}</th><th className="bz-text">{t('xBiz.channel')}</th>
                <th>{t('xBiz.spend')}</th><th>{t('xBiz.spendMoM')}</th><th>{t('xBiz.impressions')}</th><th>{t('xBiz.clicks')}</th>
                <th>{t('xBiz.ctr')}</th><th>{t('xBiz.cpc')}</th><th>{t('xBiz.cpm')}</th>
                <th className="bz-sep">{t('xBiz.attrRev')}</th><th>{t('xBiz.attrOrders')}</th><th title={t('xBiz.roasHint')}>{t('xBiz.roas')} ⓘ</th>
                <th className="bz-sep">{t('xBiz.emailRev')}</th><th>{t('xBiz.sessions')}</th><th>{t('xBiz.storeRev')}</th>
              </tr>
            </thead>
            <tbody>
              {d.byPlatform.map((x) => {
                const c = mktCells(x.cur, sym);
                return (
                  <tr key={x.platform}>
                    <td className="bz-first">{x.platform}</td>
                    <td className="bz-text">{x.channel}</td>
                    <td className="bz-num">{money(x.cur.spend, sym, dec)}</td>
                    <Growth v={x.prev && x.cur.spend ? growth(x.cur.spend, x.prev.spend) : null} />
                    <td className="bz-num">{int(x.cur.impressions)}</td>
                    <td className="bz-num">{int(x.cur.clicks)}</td>
                    <td className="bz-num">{pct(c.ctr, 2)}</td>
                    <td className="bz-num">{money(c.cpc, sym, dec2)}</td>
                    <td className="bz-num">{money(c.cpm, sym, dec2)}</td>
                    <td className="bz-num bz-sep">{money(x.cur.attrRev, sym, dec)}</td>
                    <td className="bz-num">{int(x.cur.attrOrders)}</td>
                    <td className="bz-num">{c.roas == null ? '-' : c.roas.toFixed(2)}</td>
                    <td className="bz-num bz-sep">{money(x.cur.emailRev, sym, dec)}</td>
                    <td className="bz-num">{int(x.cur.sessions)}</td>
                    <td className="bz-num">{money(x.cur.storeRev, sym, dec)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>
      )}

      {(d.campaigns || []).length > 0 && (
        <Section title={t('xBiz.secCampaign')}>
          <table className="bz-table" data-bz-table="campaign">
            <thead>
              <tr>
                <th className="bz-first">{t('xBiz.market')}</th><th className="bz-text">{t('xBiz.platform')}</th>
                <th className="bz-text">{t('xBiz.campaign')}</th>
                <th>{t('xBiz.spend')}</th><th>{t('xBiz.impressions')}</th><th>{t('xBiz.clicks')}</th>
                <th>{t('xBiz.ctr')}</th><th>{t('xBiz.cpc')}</th><th>{t('xBiz.attrRev')}</th><th>{t('xBiz.roas')}</th>
              </tr>
            </thead>
            <tbody>
              {d.campaigns.map((x, i) => {
                const c = mktCells(x, sym);
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={`${x.market}|${x.platform}|${x.campaign}|${i}`}>
                    <td className="bz-first">{x.market}</td>
                    <td className="bz-text">{x.platform}</td>
                    <td className="bz-text bz-camp" title={x.campaign}>{x.campaign}</td>
                    <td className="bz-num">{money(x.spend, sym, dec)}</td>
                    <td className="bz-num">{int(x.impressions)}</td>
                    <td className="bz-num">{int(x.clicks)}</td>
                    <td className="bz-num">{pct(c.ctr, 2)}</td>
                    <td className="bz-num">{money(c.cpc, sym, dec2)}</td>
                    <td className="bz-num">{money(x.attrRev, sym, dec)}</td>
                    <td className="bz-num">{x.claimSpend ? c.roas.toFixed(2) : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>
      )}
    </div>
  );
}
